import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { receiptAPI, formatCurrency, settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Edit, Printer, Upload } from 'lucide-react';
import api from '../services/api';
import { formatDateDisplay } from '../lib/dateUtils';

export default function ReceiptView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [advanceEngineEnabled, setAdvanceEngineEnabled] = useState(true);
  const [companyLogo, setCompanyLogo] = useState(null);
  const logoRef = useRef(null);

  const loadCompanyLogo = () => {
    api.get('/company-profile').then(res => {
      if (res.data?.logo_url) setCompanyLogo(`${res.data.logo_url}?t=${Date.now()}`);
    }).catch(() => {});
  };

  useEffect(() => {
    receiptAPI.get(id).then(res => setReceipt(res.data)).catch(() => { toast.error('Not found'); navigate('/receipts'); }).finally(() => setLoading(false));
    settingsAPI.getAdvanceEngine().then(r => setAdvanceEngineEnabled(r.data?.enabled !== false)).catch(() => {});
    loadCompanyLogo();
  }, [id, navigate]);

  const downloadPDF = async () => {
    try {
      const res = await receiptAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${receipt.receipt_number}.pdf`; a.click();
      toast.success('PDF downloaded');
    } catch { toast.error('Download failed'); }
  };

  const printPDF = async () => {
    try {
      const res = await receiptAPI.downloadPDF(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) { win.onload = () => { win.focus(); win.print(); }; }
    } catch { toast.error('Print failed'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/company-profile/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Company logo updated! New PDFs will use this logo.');
      loadCompanyLogo();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  if (!receipt) return null;

  return (
    <div data-testid="receipt-view-page" className="max-w-4xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/receipts')} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <div>
            <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">{receipt.receipt_number}</h1>
            <p className="text-sm text-slate-500">Loading Receipt &middot; {formatDateDisplay(receipt.booking_date)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <label className="btn-secondary flex items-center gap-2 cursor-pointer text-xs" data-testid="upload-logo-btn">
            <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Logo'}
            <input ref={logoRef} type="file" accept=".jpg,.jpeg,.png,.svg,.webp,image/jpeg,image/png,image/svg+xml,image/webp" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
          </label>
          <button onClick={() => navigate(`/receipts/${id}/edit`)} className="btn-secondary flex items-center gap-2"><Edit className="w-4 h-4" /> Edit</button>
          <button onClick={printPDF} className="btn-secondary flex items-center gap-2" data-testid="print-btn"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={downloadPDF} className="btn-primary flex items-center gap-2" data-testid="download-pdf-btn"><FileText className="w-4 h-4" /> Download PDF</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
        <div className="bg-slate-900 text-white p-5 flex items-center gap-4">
          <img src={companyLogo || '/images/transsolution_logo.png'} alt="Logo" data-testid="lr-company-logo" onError={(e) => { e.target.src = '/images/transsolution_logo.png'; }} className="w-12 h-12 rounded-md object-contain bg-white p-0.5" />
          <div>
            <h2 className="font-heading font-bold text-lg">{(user?.company?.name || 'Company').toUpperCase()}</h2>
            <p className="text-slate-400 text-xs">LOADING RECEIPT</p>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            {[['Receipt #', receipt.receipt_number],['Bkg. Date', receipt.booking_date],['Payment', receipt.payment_type],['Transport', receipt.transport_mode],['Service', receipt.service_type],['Risk', receipt.risk_type],['Pickup/Delivery', receipt.pickup_delivery],['Billing Party', receipt.billing_party],['Cart/Vehicle No.', receipt.cart_vehicle_no],['Vehicle Type', receipt.vehicle_type],['Driver Name', receipt.driver_name],['Driver Number', receipt.driver_number],['Created By', receipt.created_by_name]].map(([l,v]) => v ? <div key={l} className="min-w-0"><p className="text-xs text-slate-500 uppercase">{l}</p><p className="font-mono text-sm break-all">{v}</p></div> : null)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-md p-4"><p className="text-xs font-semibold text-slate-500 uppercase mb-2">Consignor</p><p className="font-medium">{receipt.consignor_name}</p><p className="text-sm text-slate-600">{receipt.consignor_address}</p><p className="text-xs text-slate-500 mt-1">GSTIN: {receipt.consignor_gstin || 'N/A'} | Mobile: {receipt.consignor_mobile || 'N/A'}</p></div>
            <div className="border border-slate-200 rounded-md p-4"><p className="text-xs font-semibold text-slate-500 uppercase mb-2">Consignee</p><p className="font-medium">{receipt.consignee_name}</p><p className="text-sm text-slate-600">{receipt.consignee_address}</p><p className="text-xs text-slate-500 mt-1">GSTIN: {receipt.consignee_gstin || 'N/A'} | Mobile: {receipt.consignee_mobile || 'N/A'}</p></div>
          </div>

          {receipt.items?.length > 0 && <div><h4 className="font-heading font-semibold text-sm mb-2">Invoice / E-Way Bill Details</h4>
            <table className="w-full data-table"><thead><tr><th>#</th><th>Invoice No.</th><th>Date</th><th className="text-right">Declared Value</th><th>E-Way Bill</th><th>E-Way Date</th></tr></thead>
            <tbody>{receipt.items.map((it,i) => <tr key={it.invoice_no || `item-${i}`}><td>{i+1}</td><td className="font-mono">{it.invoice_no}</td><td>{it.invoice_date}</td><td className="text-right font-mono">Rs.{formatCurrency(it.declared_value)}</td><td className="font-mono text-xs">{it.eway_bill_no}</td><td>{it.eway_bill_date}</td></tr>)}
            <tr className="font-bold"><td colSpan={3} className="text-right">Total Declared Value</td><td className="text-right font-mono">Rs.{formatCurrency(receipt.total_declared_value)}</td><td colSpan={2}></td></tr></tbody></table></div>}

          {receipt.packages?.length > 0 && <div><h4 className="font-heading font-semibold text-sm mb-2">Packaging</h4>
            <table className="w-full data-table"><thead><tr><th>#</th><th>Pkgs</th><th>Actual Wt</th><th>Dimensions</th><th>Vol. Wt</th><th>Charge Wt</th></tr></thead>
            <tbody>{receipt.packages.map((p,i) => <tr key={`pkg-${i}-${p.count}`}><td>{i+1}</td><td className="font-mono">{p.count}</td><td className="font-mono">{p.actual_weight} kg</td><td>{p.dimensions}</td><td className="font-mono">{p.vol_weight}</td><td className="font-mono">{p.charge_weight}</td></tr>)}</tbody></table></div>}

          {receipt.products?.length > 0 && receipt.products.some(p => p.article_name) && <div><h4 className="font-heading font-semibold text-sm mb-2">Products</h4>
            <table className="w-full data-table"><thead><tr><th>#</th><th>Article</th><th>Brand</th><th>Qty</th><th>Gender</th><th>Size</th></tr></thead>
            <tbody>{receipt.products.map((p,i) => <tr key={p.article_name ? `${p.article_name}-${p.brand_name}` : `prod-${i}`}><td>{i+1}</td><td>{p.article_name}</td><td>{p.brand_name}</td><td className="font-mono">{p.quantity}</td><td>{p.gender}</td><td>{p.size}</td></tr>)}</tbody></table></div>}

          {receipt.remarks && <div><p className="text-xs text-slate-500 uppercase mb-1">Remarks</p><p className="text-sm text-slate-600">{receipt.remarks}</p></div>}

          {/* Advance Payment & Remaining Payment - only show when advance engine is enabled and values exist */}
          {advanceEngineEnabled && ((receipt.advance_amount || 0) > 0 || (receipt.advance_adjusted || 0) > 0) && (
            <div className="border-t border-slate-200 pt-3" data-testid="receipt-advance-section">
              <h4 className="font-heading font-semibold text-sm mb-2">Payment Details</h4>
              <div className="grid grid-cols-3 gap-3">
                {(receipt.advance_amount || 0) > 0 && (
                  <div className="border border-blue-200 rounded-md p-3 text-center bg-blue-50/50">
                    <p className="text-xs text-blue-600 uppercase font-semibold">Advance Paid</p>
                    <p className="font-mono font-bold text-lg text-blue-700">Rs. {formatCurrency(receipt.advance_amount)}</p>
                  </div>
                )}
                {(receipt.advance_adjusted || 0) > 0 && (
                  <div className="border border-purple-200 rounded-md p-3 text-center bg-purple-50/50">
                    <p className="text-xs text-purple-600 uppercase font-semibold">Advance Adjusted</p>
                    <p className="font-mono font-bold text-lg text-purple-700">Rs. {formatCurrency(receipt.advance_adjusted)}</p>
                  </div>
                )}
                {((receipt.total_declared_value || 0) - (receipt.advance_amount || 0) - (receipt.advance_adjusted || 0)) > 0 && (
                  <div className="border border-green-200 rounded-md p-3 text-center bg-green-50/50">
                    <p className="text-xs text-green-600 uppercase font-semibold">Remaining</p>
                    <p className="font-mono font-bold text-lg text-green-700">Rs. {formatCurrency((receipt.total_declared_value || 0) - (receipt.advance_amount || 0) - (receipt.advance_adjusted || 0))}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 pt-3 text-center">
            <p className="text-[10px] text-slate-400">Generated by Transsolution &copy; 2026 {user?.company?.name || ''}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
